import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { RollText } from '@/components/generals/roll-text'
import { Wordmark } from '@/components/generals/wordmark'

import { CONTACT } from '@/constants/site'
import { NAV_CONTACT, NAV_MAIN } from '@/constants/navigation'

type Theme = 'light' | 'dark'

const NAV_PROBE_Y = 40

const isActive = (pathname: string, href: string): boolean => pathname === href || pathname.startsWith(`${href}/`)

export const Navbar: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [theme, setTheme] = useState<Theme>('light')
    const [scrolled, setScrolled] = useState(false)

    useEffect(() => {
        let frame = 0

        const update = () => {
            frame = 0
            setScrolled(window.scrollY > 16)

            let next: Theme = 'light'

            document.querySelectorAll<HTMLElement>('[data-nav-theme]').forEach((section) => {
                const rect = section.getBoundingClientRect()

                if (rect.top <= NAV_PROBE_Y && rect.bottom > NAV_PROBE_Y) {
                    next = section.dataset.navTheme === 'dark' ? 'dark' : 'light'
                }
            })

            setTheme(next)
        }

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            if (frame) cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [router.asPath])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        const previous = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open])

    const dark = open || theme === 'dark'

    return (
        <>
            <header className='fixed inset-x-0 top-0 z-50'>
                <div className='mx-auto max-w-[1440px] px-3 pt-3 sm:px-5 md:px-8 md:pt-4'>
                    <nav
                        aria-label='Main'
                        className={cn(
                            'flex h-14 items-center justify-between rounded-sm border px-4 backdrop-blur-md transition-[background-color,border-color,color,box-shadow] duration-500 md:h-16 md:px-5',
                            dark ? 'border-cream/15 bg-charcoal/90 text-cream' : 'border-charcoal/15 bg-cream/90 text-charcoal',
                            scrolled && !open && 'shadow-[0_16px_40px_-20px_rgba(37,37,34,0.45)]'
                        )}
                    >
                        <Link
                            href='/'
                            aria-label='BLITZ home'
                            className='rounded-sm transition-opacity duration-300 hover:opacity-75 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-coral'
                        >
                            <Wordmark className='text-[1.65rem] md:text-[1.85rem]' />
                        </Link>

                        <ul className='hidden items-center lg:flex'>
                            {NAV_MAIN.map((link, index) => {
                                const active = isActive(router.pathname, link.href)

                                return (
                                    <li key={link.href} className='flex items-center'>
                                        {index > 0 && <span aria-hidden='true' className='h-3 w-px bg-current opacity-25' />}
                                        <Link
                                            href={link.href}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'group relative flex items-center gap-2 px-5 py-2 text-[12px] font-medium uppercase tracking-[0.14em] transition-opacity duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral',
                                                active ? 'opacity-100' : 'opacity-75 hover:opacity-100'
                                            )}
                                        >
                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'size-1.5 bg-coral transition-transform duration-300',
                                                    active ? 'scale-100' : 'scale-0 group-hover:scale-100'
                                                )}
                                            />
                                            <RollText>{link.label}</RollText>
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>

                        <div className='flex items-center gap-2 sm:gap-4'>
                            <Link
                                href={NAV_CONTACT.href}
                                aria-current={isActive(router.pathname, NAV_CONTACT.href) ? 'page' : undefined}
                                className={cn(
                                    'group hidden h-10 items-center gap-2 rounded-sm bg-coral px-4 text-[12px] font-semibold uppercase tracking-[0.1em] text-charcoal transition-colors duration-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral sm:inline-flex',
                                    dark ? 'hover:bg-cream' : 'hover:bg-charcoal hover:text-cream'
                                )}
                            >
                                <RollText>Let&apos;s talk</RollText>
                                <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:translate-x-1' />
                            </Link>

                            <button
                                type='button'
                                onClick={() => setOpen((value) => !value)}
                                aria-expanded={open}
                                aria-controls='mobile-menu'
                                aria-label={open ? 'Close menu' : 'Open menu'}
                                className='flex size-10 items-center justify-center rounded-sm border border-current/25 transition-colors duration-300 hover:bg-coral hover:text-charcoal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral lg:hidden'
                            >
                                {open ? <X className='size-5' /> : <Menu className='size-5' />}
                            </button>
                        </div>
                    </nav>
                </div>
            </header>

            <div
                id='mobile-menu'
                inert={!open}
                className={cn(
                    'fixed inset-0 z-40 flex flex-col bg-charcoal text-cream transition-[opacity,visibility] duration-500 lg:hidden',
                    open ? 'visible opacity-100' : 'invisible opacity-0'
                )}
            >
                <div className='flex flex-1 flex-col justify-between overflow-y-auto px-5 pb-8 pt-28 sm:px-8'>
                    <ul>
                        {NAV_MAIN.map((link, index) => {
                            const active = isActive(router.pathname, link.href)

                            return (
                                <li
                                    key={link.href}
                                    style={{ transitionDelay: open ? `${120 + index * 60}ms` : '0ms' }}
                                    className={cn(
                                        'border-b border-cream/15 transition-[opacity,translate] duration-500',
                                        open ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                                    )}
                                >
                                    <Link
                                        href={link.href}
                                        aria-current={active ? 'page' : undefined}
                                        onClick={() => setOpen(false)}
                                        className='flex items-baseline justify-between py-4 font-heading font-wide text-[clamp(1.75rem,8vw,2.75rem)] font-black uppercase leading-none tracking-[-0.02em] transition-colors hover:text-coral'
                                    >
                                        <span className={cn(active && 'text-coral')}>{link.label}</span>
                                        <span className='font-mono text-[11px] font-normal tracking-[0.2em] text-cream/60'>
                                            0{index + 1}
                                        </span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='mt-10 space-y-6'>
                        <Link
                            href='/contact'
                            onClick={() => setOpen(false)}
                            className='flex h-14 w-full items-center justify-center gap-3 rounded-sm bg-coral text-[13px] font-semibold uppercase tracking-[0.1em] text-charcoal transition-colors hover:bg-cream'
                        >
                            Let&apos;s talk
                            <ArrowRight aria-hidden='true' className='size-4' />
                        </Link>

                        {CONTACT.email && (
                            <a href={`mailto:${CONTACT.email}`} className='block break-all font-mono text-[11px] uppercase tracking-[0.14em] text-cream/70 hover:text-coral'>
                                {CONTACT.email}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
